import { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { Alert, Card, Empty, Input, Space, Spin, Typography, message } from 'antd';
import {
  EditOutlined,
  ClockCircleOutlined,
  EnvironmentOutlined,
  FileTextOutlined,
  HistoryOutlined,
  InfoCircleOutlined,
  SolutionOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { getFormGroups } from '../services/dropdownApi';
import { loadDetailDefaults, getDefaults } from '../services/detailDefaults';
import { getModuleDocuments } from '../services/documentApi';
import { getModuleDataList } from '../services/moduleDataApi';
import { fetchJsonWithAuth } from '../services/authApi';
import { AUTH_URL } from '../services/apiConfig';
import { updateJobDescription } from '../services/jobsApi';
import { getDeep } from './form/payloadTransformer';
import { extractDocuments } from './detail/renderConfig';
import { hasResolvedDocuments, skipEmptyCell } from './detail/detailEmptiness';
import { displayPhoneForField, countryCodeKeys } from './detail/phoneDisplay';
import { fetchUserName, indexUserRows } from './detail/userNames';
import { recordValues, visibleInDetail, detailFieldLabel } from './detail/detailVisibility';
import { readHistoryRateParts } from './detail/historyRateKeys';
import { currencySymbol, formatAmount, rateUnitLabel } from '../utils/currency';
import AppButton from './AppButton';
import FieldValue from './detail/fieldRenderEngine';
import { resolveGroupEmptyText } from './detail/emptyText';
import { formatAppDateTime } from '../services/timezone';


const { Text } = Typography;

const sectionIcons = [
  <UserOutlined />,
  <EnvironmentOutlined />,
  <SolutionOutlined />,
  <InfoCircleOutlined />,
  <FileTextOutlined />,
];

const truthy = (value) => value === true || value === 1 || value === '1';

// Field keys that hold the job's free-text description (whichever the API uses).
const DESCRIPTION_KEYS = ['jobDescription', 'description', 'rawDescription', 'jobDescriptionText'];

// Pull the description field out of the raw form-groups (jobs module only).
function extractDescription(formGroups = []) {
  for (const group of formGroups) {
    for (const field of group.fields ?? []) {
      const matchesKey = DESCRIPTION_KEYS.includes(field.field);
      const matchesLabel = /description/i.test(field.label ?? '');
      if ((matchesKey || matchesLabel) && typeof field.value === 'string' && field.value.trim()) {
        return { ...field, key: field.field, value: field.value };
      }
    }
  }
  return null;
}

function normalizeGroups(formGroups = []) {
  const values = recordValues(formGroups);
  return [...formGroups]
    // hideGroup hides everywhere (same flag as the forms); hideGroupInDetail is
    // the detail-only variant — the add/edit forms never read it.
    .filter((group) => !truthy(group.hideGroup) && !truthy(group.hideGroupInDetail))
    .filter((group) => visibleInDetail(group, values))
    .sort((a, b) => Number(a.order ?? 0) - Number(b.order ?? 0))
    .map((group) => ({
      ...group,
      fields: [...(group.fields ?? [])]
        .filter((field) => String(field.field ?? '').trim() !== ''
          && (field.show === undefined || truthy(field.show))
          && visibleInDetail(field, values))
        // Resolve the Conditional Label against this record, so the detail view
        // reads the same as the add/edit form did for the same contract type.
        .map((field) => ({ ...field, label: detailFieldLabel(field, values) }))
        .sort((a, b) => Number(a.order ?? 0) - Number(b.order ?? 0)),
    }))
    .filter((group) => group.fields.length);
}

function rowsFromResponse(response) {
  const payload = response?.data ?? response;
  if (Array.isArray(payload)) return payload;
  return payload?.data ?? payload?.rows ?? payload?.records ?? payload?.items ?? payload?.list ?? [];
}

function getValue(record, ...keys) {
  for (const key of keys) {
    const value = String(key).split('.').reduce((current, part) => current?.[part], record);
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return '';
}

function activeBatchAssignment(record) {
  const deleted = String(getValue(record, 'isDeleted', 'deleted')).toLowerCase();
  const status = String(getValue(record, 'assignmentStatus', 'status')).toLowerCase();
  return deleted !== 'true' && deleted !== '1' && !['removed', 'inactive', 'deleted'].includes(status);
}

function recordIdsFromGroups(formGroups = [], id) {
  const ids = {
    candidateIds: new Set(),
    engagementIds: new Set([String(id ?? '')].filter(Boolean)),
  };
  const meta = formGroups?.recordMeta;
  ['_id', 'id', 'workforceEngagementId', 'engagementId'].forEach((key) => {
    const value = meta?.[key];
    if (value !== undefined && value !== null && value !== '') ids.engagementIds.add(String(value));
  });
  ['candidateId', 'candidate_id', 'applicantId'].forEach((key) => {
    const value = meta?.[key];
    if (value !== undefined && value !== null && value !== '') ids.candidateIds.add(String(value));
  });
  for (const group of formGroups ?? []) {
    for (const field of group.fields ?? []) {
      if (['candidateId', 'candidate_id', 'applicantId'].includes(field?.field) && field.value) {
        ids.candidateIds.add(String(field.value?.id ?? field.value?.value ?? field.value));
      }
      if (['workforceEngagementId', 'engagementId'].includes(field?.field) && field.value) {
        ids.engagementIds.add(String(field.value?.id ?? field.value?.value ?? field.value));
      }
    }
  }
  return ids;
}

async function resolveBatchName(assignment) {
  const explicitName = getValue(assignment, 'batchName', 'batch_name', 'batch.name', 'batch.title');
  if (explicitName) return String(explicitName);

  const batchId = String(getValue(assignment, 'batchId', 'batch_id'));
  if (!batchId) return '';

  for (const moduleName of ['batches', 'batch']) {
    try {
      const rows = rowsFromResponse(await getModuleDataList(moduleName, 500, 0));
      const batch = rows.find((record) => String(getValue(record, '_id', 'id', 'batchId', 'batch_id')) === batchId);
      const name = getValue(batch, 'batchName', 'batch_name', 'name', 'title');
      if (name) return String(name);
    } catch {
      // Some projects register the batch module under only one of these names.
    }
  }
  return batchId;
}

async function appendBatchGroup(formGroups, id, moduleName) {
  const normalizedModule = String(moduleName ?? '').trim().toLowerCase();
  if (!['candidate', 'candidates', 'onboarding'].includes(normalizedModule)) return formGroups;

  const { candidateIds, engagementIds } = recordIdsFromGroups(formGroups, id);
  if (!candidateIds.size && !engagementIds.size) return formGroups;

  const assignments = rowsFromResponse(await getModuleDataList('batchCandidates', 1000, 0))
    .filter(activeBatchAssignment);
  const assignment = assignments.find((record) => (
    candidateIds.has(String(getValue(record, 'candidateId', 'candidate_id')))
    || engagementIds.has(String(getValue(record, 'workforceEngagementId', 'engagementId')))
  ));
  if (!assignment) return formGroups;

  const name = await resolveBatchName(assignment);
  if (!name) return formGroups;

  return [
    ...formGroups,
    {
      id: 'candidate-batch-summary',
      name: 'candidateBatch',
      label: 'Batch',
      order: 9999,
      fields: [
        {
          field: 'batchName',
          label: 'Batch Name',
          type: 'text',
          value: name,
          show: true,
          order: 1,
        },
      ],
    },
  ];
}

function SectionTitle({ icon, title }) {
  return (
    <Space size={10} align="center">
      <div className="v1-section-icon-box">{icon}</div>
      <Text strong className="v1-section-label">{title}</Text>
    </Space>
  );
}

function RawDescription({ value }) {
  const text = String(value ?? '');
  if (/<[a-z][\s\S]*>/i.test(text)) {
    return (
      <div
        className="v1-detail-value"
        style={{ whiteSpace: 'normal', lineHeight: 1.7 }}
        dangerouslySetInnerHTML={{ __html: text }}
      />
    );
  }
  return (
    <div className="v1-detail-value" style={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
      {text}
    </div>
  );
}

/*
 * DetailViewV1 — renders ONLY the left column of a detail view: the grouped
 * field cards plus (for jobs) the parsed Description card.
 *
 * Self-contained: given `moduleName` + `id` it fetches its own form-groups
 * (the backend embeds each field's value when an id is supplied) and manages
 * its own loading/error state. DetailViewPage just drops it into the left
 * <Col> and passes moduleName + id.
 */
export default function DetailViewV1({ moduleName, id, refreshKey = 0, onDescriptionSaved }) {
  const [formGroups, setFormGroups] = useState([]);
  const [loading, setLoading] = useState(Boolean(moduleName));
  const [error, setError] = useState('');
  const [descEditing, setDescEditing] = useState(false);
  const [descEditRaw, setDescEditRaw] = useState('');
  const [descSaving, setDescSaving] = useState(false);
  // Files registered in the central `documents` collection (keyed by module +
  // record id + fieldName). Fetched alongside the form groups so document/file
  // fields open from that collection's presigned URLs — not only the inline
  // record value (which is often empty once uploads moved to the registry).
  const [centralDocs, setCentralDocs] = useState([]);

  useEffect(() => {
    if (!moduleName || !id) {
      Promise.resolve().then(() => setCentralDocs([]));
      return undefined;
    }
    let cancelled = false;
    getModuleDocuments(moduleName, id)
      .then((docs) => { if (!cancelled) setCentralDocs(Array.isArray(docs) ? docs : (docs?.data ?? [])); })
      .catch(() => { if (!cancelled) setCentralDocs([]); });
    return () => { cancelled = true; };
  }, [moduleName, id, refreshKey]);

  // ── Rate history (config-driven via the admin's appendHistory groups) ───────
  // A field the admin marked append-only (group.appendHistory + historyField/
  // historyFields) stores an ARRAY of { <rate…>, timestamp, updatedBy }. The
  // form-groups response only exposes the LATEST entry, so we fetch the raw
  // record for the full array, plus a user map to resolve updatedBy → a name.
  const [rawRecord, setRawRecord] = useState(null);
  const [userMap, setUserMap] = useState({});
  // Actors the users directory could not account for, resolved one-by-one
  // through the auth service (cached per id for the session).
  const [resolvedUsers, setResolvedUsers] = useState({});
  useEffect(() => {
    if (!moduleName || !id) { setRawRecord(null); return undefined; }
    let cancelled = false;
    fetchJsonWithAuth(AUTH_URL, `/module/list?module=${encodeURIComponent(moduleName)}&id=${encodeURIComponent(id)}`)
      .then((res) => {
        const rows = res?.data?.data ?? res?.data ?? [];
        if (!cancelled) setRawRecord(Array.isArray(rows) ? rows[0] : null);
      })
      .catch(() => { if (!cancelled) setRawRecord(null); });
    return () => { cancelled = true; };
  }, [moduleName, id, refreshKey]);

  // The users directory, indexed on EVERY id a row carries (legacyUserId /
  // userId / _id / id — see detail/userNames.js). Indexing on legacyUserId
  // alone is what made an auth-stamped `updatedBy` fall through to "User #123".
  useEffect(() => {
    let cancelled = false;
    fetchJsonWithAuth(AUTH_URL, '/module/list?module=users')
      .then((res) => {
        const rows = res?.data?.data ?? res?.data ?? [];
        if (!cancelled) setUserMap(indexUserRows(rows));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!moduleName) return undefined;

    let cancelled = false;

    async function fetchFormGroups() {
      try {
        setLoading(true);
        setError('');
        // Passing id makes the backend embed each field's actual record value.
        // view:'detail' makes the backend apply the admin Detail-Groups config
        // (field overrides + merges) and shape reference/document fields as
        // objects. Load the display defaults in parallel so FieldValue renders
        // with the configured S3 base / date format on first paint.
        const [data] = await Promise.all([
          getFormGroups({ module: moduleName, id, view: 'detail' }),
          loadDetailDefaults(),
        ]);
        const groupsWithBatch = await appendBatchGroup(
          Array.isArray(data) ? data : [],
          id,
          moduleName,
        );
        if (!cancelled) setFormGroups(groupsWithBatch);
      } catch (err) {
        console.error(`Failed to fetch ${moduleName} detail fields:`, err);
        if (!cancelled) setError(err?.message || 'Failed to fetch detail fields');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchFormGroups();

    return () => {
      cancelled = true;
    };
  }, [moduleName, id, refreshKey]);

  const groups = useMemo(() => normalizeGroups(formGroups), [formGroups]);

  // The append-history containers this record has, per the admin's config.
  // Shared by the actor-resolution effect and the history blocks themselves so
  // both always look at exactly the same set of arrays.
  const historyKeys = useMemo(() => {
    const keys = new Set();
    formGroups.forEach((g) => {
      if (truthy(g.appendHistory)) {
        [g.historyField, ...(g.historyFields || [])].filter(Boolean).forEach((k) => keys.add(k));
      }
    });
    return keys;
  }, [formGroups]);

  // Resolve every history actor the users directory did not cover. One call per
  // DISTINCT unknown id (cached in detail/userNames.js), so a long negotiation
  // history costs nothing extra, and a failure resolves to '' — the row then
  // renders without attribution rather than with a meaningless "User #123".
  useEffect(() => {
    if (!rawRecord || !historyKeys.size) return undefined;
    const unknown = new Set();
    historyKeys.forEach((key) => {
      const arr = rawRecord[key];
      if (!Array.isArray(arr)) return;
      arr.forEach((entry) => {
        const by = entry?.updatedBy ?? entry?.changedBy;
        if (by === undefined || by === null || by === '') return;
        const id = String(by);
        if (!userMap[id] && !resolvedUsers[id]) unknown.add(id);
      });
    });
    if (!unknown.size) return undefined;
    let cancelled = false;
    Promise.all([...unknown].map((id) => fetchUserName(id).then((name) => [id, name])))
      .then((pairs) => {
        const found = Object.fromEntries(pairs.filter(([, name]) => name));
        if (!cancelled && Object.keys(found).length) {
          setResolvedUsers((prev) => ({ ...prev, ...found }));
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [rawRecord, historyKeys, userMap, resolvedUsers]);

  // Last-resort labels for rate-history containers. These are a FALLBACK only:
  // the real label comes from the form-group field that owns the container (see
  // historyBlockLabel), so it honours the admin's label and its Conditional
  // Label instead of being hardcoded per module here.
  const HISTORY_LABELS = { proposedRate: 'Proposed Rate', candidateBudget: 'Candidate Rate' };

  // historyBlockLabel — the heading for one history container, resolved from
  // admin config in priority order:
  //   1. group.historyLabels[key]        explicit static label
  //   2. group.historyLabelFrom[key]     the field whose label titles it
  //   3. the first field under "<key>."  (every group authored so far)
  //   4. HISTORY_LABELS / the raw key    last-resort fallback
  //
  // 2 and 3 both go through detailFieldLabel, so the heading honours the
  // field's Conditional Label and re-titles itself per record — "Employer Rate"
  // on a C2C submission, "Candidate Rate" on a W2 one.
  const historyBlockLabel = (key, groupsForLabels, values) => {
    const prefix = `${key}.`;
    const owns = (group) => [group?.historyField, ...(group?.historyFields || [])].includes(key);

    for (const group of groupsForLabels ?? []) {
      if (!owns(group)) continue;
      const explicit = group.historyLabels?.[key];
      if (explicit) return explicit;
      const fromKey = group.historyLabelFrom?.[key];
      if (fromKey) {
        const named = (group.fields ?? []).find((f) => f?.field === fromKey);
        if (named) return detailFieldLabel(named, values) || HISTORY_LABELS[key] || key;
      }
    }
    for (const group of groupsForLabels ?? []) {
      for (const field of group?.fields ?? []) {
        if (String(field?.field ?? '').startsWith(prefix)) {
          return detailFieldLabel(field, values) || HISTORY_LABELS[key] || key;
        }
      }
    }
    return HISTORY_LABELS[key] || key;
  };
  // Format ONE history entry of `container` into { amount, unit, when, by }.
  //
  // WHICH sub-key holds the amount (and its currency / unit siblings) is NOT
  // guessed here any more — see detail/historyRateKeys.js. The old guess was
  // "the first key matching rate|budget|amount that holds a number", and on a
  // stored candidateBudget entry the first such key is `candidateBudgetEnd: 0`
  // (Mongo returns keys in insertion order), so a rate saved as 92 rendered as
  // "$0". The admin config already identifies the amount: the group that
  // declares the container configures exactly the "<container>.<subKey>" fields
  // that matter, and the End bound is not among them.
  //
  // Precedence is override → configured fields → the old regex (fallback only,
  // so containers whose group configures no sub-fields are untouched). Still
  // fully generic: no module, container or field name appears here.
  const formatHistoryEntry = (entry, container) => {
    const { amount, currency, unit } = readHistoryRateParts(entry, container, formGroups);
    const rawTs = entry?.timestamp?.$date ?? entry?.timestamp ?? entry?.updatedAt?.$date
      ?? entry?.updatedAt ?? entry?.changedAt?.$date ?? entry?.changedAt;
    const by = entry?.updatedBy ?? entry?.changedBy;
    const amountNum = amount === undefined || amount === null || amount === ''
      ? null : Number(amount);
    // Normalise currency to a SYMBOL so every row reads the same ("$100"), and
    // the unit to a consistent duration word ("Hourly"), so candidate + proposed
    // rows share one "$value / Duration" format regardless of how each was saved.
    // Both come from utils/currency — the one place that knows these rules, and
    // the same one the detail field engine and the finance panel render through.
    return {
      amount: amountNum != null && !Number.isNaN(amountNum)
        ? `${currencySymbol(currency)}${formatAmount(amountNum)}` : '—',
      unit: rateUnitLabel(unit),
      when: rawTs && dayjs(rawTs).isValid()
        // Tenant clock + zone name, like every other moment in the app.
        ? formatAppDateTime(rawTs) : '',
      // WHO, or nothing. An id we cannot turn into a person is not information
      // — "User #123" told the recruiter nothing and looked like a defect — so
      // the attribution chip is simply omitted for an unresolvable actor.
      by: by != null ? (userMap[String(by)] || resolvedUsers[String(by)] || '') : '',
    };
  };
  const historyBlocks = useMemo(() => {
    if (!rawRecord) return [];
    // Same value source the field labels use, so a history block and its field
    // can never disagree about what the record says.
    const values = recordValues(formGroups);
    const blocks = [];
    historyKeys.forEach((key) => {
      const arr = rawRecord[key];
      if (!Array.isArray(arr) || arr.length === 0) return;
      // Only real, attributed changes: an entry must carry a timestamp (a
      // stray push with no timestamp/updatedBy is an incomplete duplicate, not
      // a distinct rate change).
      const valid = arr.filter((e) => e && (e.timestamp || e.updatedAt || e.changedAt));
      if (valid.length === 0) return;
      blocks.push({
        key,
        label: historyBlockLabel(key, formGroups, values),
        // newest first. The container is passed through so each entry resolves
        // its amount/currency/unit against the config of the group that owns it.
        entries: [...valid].reverse().map((entry) => formatHistoryEntry(entry, key)),
      });
    });
    return blocks;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawRecord, formGroups, historyKeys, userMap, resolvedUsers]);

  // An appendHistory container is ALREADY shown by the synthetic Rate Details
  // card below — richer than a plain field row, since it carries the timeline,
  // who changed it and when. Rendering the owning group's fields as well prints
  // the same rate twice under two identically-titled cards.
  //
  // So: strip the covered fields from the group that declares the container,
  // and drop the group entirely once nothing else is left in it. Fields in that
  // group that are NOT part of a container still render normally, and groups
  // without appendHistory are untouched.
  //
  // This is what `hideGroupInDetail` was standing in for on the submission rate
  // group — a manual switch that also hid any future non-history field in it,
  // and had to be remembered for every new appendHistory group.
  const displayGroups = useMemo(() => {
    const covered = new Set(historyBlocks.map((b) => b.key));
    if (!covered.size) return groups;
    return groups
      .map((group) => {
        if (!truthy(group.appendHistory)) return group;
        const owned = [group.historyField, ...(group.historyFields || [])]
          .filter((k) => k && covered.has(k));
        if (!owned.length) return group;
        return {
          ...group,
          fields: group.fields.filter(
            (f) => !owned.some((k) => String(f.field ?? '').startsWith(`${k}.`)),
          ),
        };
      })
      .filter((group) => group.fields.length);
  }, [groups, historyBlocks]);

  // Title the synthetic card from the group that declares the history, instead
  // of a hardcoded module-specific string, so renaming the group in admin
  // renames the card.
  const historyCardTitle = useMemo(() => {
    const covered = new Set(historyBlocks.map((b) => b.key));
    const owner = formGroups.find((g) => truthy(g.appendHistory)
      && [g.historyField, ...(g.historyFields || [])].some((k) => k && covered.has(k)));
    return owner?.label ?? owner?.name ?? 'Rate Details';
  }, [formGroups, historyBlocks]);

  const isJob = moduleName === 'job';
  // For jobs we render the description in its own dedicated section (below),
  // so we keep it out of the normal field grid.
  const jobDescription = useMemo(
    () => (isJob ? extractDescription(formGroups) : null),
    [isJob, formGroups],
  );

  const handleDescEditCancel = () => {
    setDescEditing(false);
    setDescEditRaw('');
  };

  const handleDescEditSave = async () => {
    if (!descEditRaw.trim()) {
      message.warning('Please enter a job description');
      return;
    }
    setDescSaving(true);
    try {
      await updateJobDescription(id, descEditRaw.trim());
      setDescEditing(false);
      setDescEditRaw('');
      setLoading(true);
      const data = await getFormGroups({ module: moduleName, id, view: 'detail' });
      setFormGroups(Array.isArray(data) ? data : []);
      message.success('Description updated and parsed successfully');
      onDescriptionSaved?.();
    } catch {
      message.error('Failed to update description');
    } finally {
      setDescSaving(false);
      setLoading(false);
    }
  };

  // renderField renders one labelled cell. `cellField` already carries its value
  // (the field config for flat groups; a per-row pseudo-field for addRow rows).
  // The detail grid is a UNIFORM CSS grid, not Ant's 24-column Row/Col.
  //
  // It used to size each cell from `detailCol ?? col ?? 8` — and `col` is the
  // FORM's width, chosen for INPUT controls. Read-only label/value pairs have
  // completely different needs, so the detail view inherited widths of 4, 6, 8
  // and 12 that never tile evenly in a 24-column grid (8+8+4 = 20, 12+8 = 20 …),
  // leaving ragged half-empty rows and 4-wide cells too narrow for their label.
  //
  // Now every cell is one equal track (auto-fill, min 220px), so fields always
  // line up in clean rows at any width. `detailCol` remains the admin's explicit
  // override — it maps to a column SPAN, so wide content (a description, a
  // document list) can still take more room without breaking the rhythm.
  const cellSpan = (cellField) => {
    const explicit = Number(cellField.detailCol);
    if (Number.isFinite(explicit) && explicit > 0) {
      // Config speaks in 24ths; the grid speaks in tracks (3 per row at the
      // reference width). Round so 24 → full row, 12 → half, 8 → one track.
      return Math.max(1, Math.min(3, Math.round(explicit / 8)));
    }
    // Content that is inherently long gets the full row instead of being
    // squeezed into one track and wrapping into a tall, ragged cell.
    const text = typeof cellField.value === 'string' ? cellField.value : '';
    if (cellField.renderType === 'document' || cellField.type === 'text-editor' || text.length > 120) {
      return 3;
    }
    // A MERGED value is several parts joined by a separator — a date range
    // ("13 Jul 2026 - 17 Jul 2026") or a rate ("300 · USD · Hourly"). In one
    // track those wrap onto three lines and read as broken data, so give them
    // two. Deliberately narrow: only multi-part values that are actually too
    // long for a single track, so ordinary short values keep the tight rhythm.
    const merged = /\s(?:·|-|–|\/)\s/.test(text);
    return merged && text.length >= 16 ? 2 : 1;
  };

  // Prose wraps; an atomic token (phone, date range, rate, id) never does — it
  // ellipsises instead, with the full value on hover. Anything genuinely
  // sentence-like, or rendered as a document/rich text, opts back into wrapping.
  const wantsWrap = (cellField) => {
    if (cellField.renderType === 'document' || cellField.type === 'text-editor') return true;
    // Length ALONE. A token count would misread "13 Jul 2026 - 17 Jul 2026"
    // (seven tokens, one atomic value) as prose.
    const text = typeof cellField.value === 'string' ? cellField.value : '';
    return text.length > 60;
  };

  // The record's own flattened values — what a render type consults when it
  // needs a SIBLING key (e.g. the phone renderer's country code). Inside a
  // repeatable group the ROW is passed instead, so each row speaks for itself.
  const recordValueMap = useMemo(() => recordValues(formGroups), [formGroups]);

  const renderField = (cellField, keySuffix = '', record = recordValueMap) => {
    const label = cellField.label ?? cellField.field;
    const wrap = wantsWrap(cellField);
    // Truncated text must never be unreachable — the title carries the full
    // value for both the label and any value clipped to one line.
    const fullValue = typeof cellField.value === 'string' ? cellField.value : undefined;
    return (
      <div
        key={`${cellField.field}${keySuffix}`}
        className={`v1-detail-cell${wrap ? ' v1-detail-cell--wrap' : ''}`}
        style={{ '--v1-cell-span': cellSpan(cellField) }}
      >
        <div className="v1-detail-field" title={!wrap && fullValue ? `${label}: ${fullValue}` : undefined}>
          <Text type="secondary" className="v1-detail-label" title={label}>
            {label}
          </Text>
          <FieldValue field={cellField} record={record} />
        </div>
      </div>
    );
  };

  // Render a list of cells honoring `combineKey`: fields that share a combineKey
  // collapse into ONE merged cell (label from the combineLabel field, value =
  // each field's display joined with " · ") — the read-side counterpart of the
  // form's combined row, so the SAME admin config drives both views identically.
  // `buildCell(field)` yields a cell-field carrying that field's `.value`.
  // Format one field's value for a merged cell the SAME way the detail engine
  // would render it standalone: dates → the common dateFormat, phones → the
  // common phone mask, everything else → trimmed text. So a combined "Start /
  // End Date" (2 parts) or a "Passport" combo (4 parts) shows formatted values,
  // never raw ISO strings.
  const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}([T ]\d{2}:\d{2}|$)/;
  const formatCellValue = (field, v, record = recordValueMap) => {
    if (v == null || typeof v === 'object') return '';
    const s = String(v).trim();
    if (!s) return '';
    if (field?.type === 'date' || field?.renderType === 'date' || ISO_DATE_RE.test(s)) {
      const d = dayjs(s);
      if (d.isValid()) return d.format(field?.renderOptions?.dateFormat || getDefaults().dateFormat);
    }
    // Same phone resolution a standalone phone cell uses — mask from the admin
    // defaults, country code from THIS record (see detail/phoneDisplay.js).
    if (field?.formatter === 'phone' || field?.type === 'phone' || field?.renderType === 'phone') {
      return displayPhoneForField(field, s, record) || s;
    }
    return s;
  };
  // partLabel — what to call ONE part of a combined block. The block already
  // carries the shared name ("Passport"), so a part only needs what is left of
  // its own label after the shared words are removed: "Passport Number" →
  // "Number", "Alt Visa Expiry" (block "Alternate Visa") → "Expiry". Matching is
  // word-wise and prefix-tolerant so an abbreviated label ("Alt" vs "Alternate")
  // still resolves. When nothing distinct remains — a "Start / End Date" range,
  // where each part's label IS the block's — the part shows plain, exactly as
  // before, so only genuinely ambiguous blocks gain labels.
  const partLabel = (field, blockLabel) => {
    // Admin opt-out (Detail Groups → "Hide this part's label"). A qualifier
    // whose value already names itself needs no sub-label: a Notice Period of
    // 12 Days printed "12 · Unit Days", because "Notice Period Unit" under the
    // block "Notice Period" leaves the word "Unit".
    // Both shapes: FormGroupField has no key of its own for a display-only
    // flag, so the backend forwards it inside renderOptions — the same carrier
    // hideWhenZero/hideWhenEmpty use. Reading only the top-level key is what
    // makes a switch like this look wired and do nothing.
    if (field?.hidePartLabel || field?.renderOptions?.hidePartLabel) return '';
    const own = String(field?.label ?? '').trim();
    if (!own) return '';
    const shared = String(blockLabel ?? '').toLowerCase().split(/[\s/]+/).filter(Boolean);
    const words = own.split(/\s+/);
    let i = 0;
    while (i < words.length) {
      const w = words[i].toLowerCase();
      if (!shared.some((s) => s === w || s.startsWith(w) || w.startsWith(s))) break;
      i += 1;
    }
    // Nothing stripped means the label shares nothing with the block name (a
    // "Country" part under "Passport") — it keeps its own label in full.
    return words.slice(i).join(' ').trim();
  };

  const renderMergedCell = (pack, labelField, buildCell, keySuffix, record = recordValueMap) => {
    // A combined pack can hold BOTH scalars and a file — the Documents group
    // combines "Passport" (the uploaded copy) with its number and expiry under
    // one label. Joining every part into a string silently DROPPED the file
    // (its value is an object, and formatCellValue only stringifies scalars),
    // so a configured document never rendered inside a combined block. Split
    // the pack instead: scalars keep the "a · b" line, file parts render
    // through the same document engine a standalone file field uses.
    const cells = pack.map((field) => ({ field, cell: buildCell(field) }));
    // A file part is only a part when there is a FILE. A "Passport" pack whose
    // record holds just a number and an expiry still carries the container
    // object, and extractDocuments turns any object into one nameless
    // "Document" — so the block printed a file chip for a file nobody uploaded.
    // hasResolvedDocuments counts only documents that resolve to something
    // openable (a storage location or a URL), which is the honest test.
    const docCells = cells
      .filter(({ cell }) => isFileField(cell))
      .map(({ cell }) => ({ ...cell, renderType: 'document' }))
      .filter((cell) => hasResolvedDocuments(cell.value));
    // A phone-formatted field folds its country-code SIBLING into its own
    // displayed text (formatCellValue → displayPhoneForField, e.g. "+1
    // 555-1234"). When that sibling is ALSO its own part of this same
    // combined pack — the ordinary "code | number" combine layout — showing
    // it again prints the code twice ("+1  ·  +1 555-1234"). Drop any pack
    // field that is such an absorbed country-code companion of another field
    // in the SAME pack.
    const absorbedKeys = new Set();
    cells.forEach(({ field }) => {
      const isPhone = field?.formatter === 'phone' || field?.type === 'phone' || field?.renderType === 'phone';
      if (!isPhone) return;
      const candidates = [field?.renderOptions?.countryCodeField ?? field?.countryCodeField, ...countryCodeKeys(field)]
        .filter(Boolean);
      cells.forEach(({ field: sibling }) => {
        if (sibling === field) return;
        if (candidates.includes(sibling.field)) absorbedKeys.add(sibling.field);
      });
    });
    // Mirror the form: the pack's label is combineLabel (a string) when set,
    // else the label-owning field's label.
    const labelText = typeof labelField.combineLabel === 'string' && labelField.combineLabel.trim()
      ? labelField.combineLabel
      : (labelField.label ?? labelField.field);
    const parts = cells
      .filter(({ cell }) => !isFileField(cell))
      .filter(({ field }) => !absorbedKeys.has(field.field))
      // Per-field `hideWhenEmpty` / `hideWhenZero` (Detail Groups) INSIDE the
      // block, not only on a standalone cell: the end of a start/end range that
      // was never filled in stores 0, and a "92 · 0" reads as a range that was
      // priced at nothing. Dropping the part here — before the separators are
      // laid down below, which are index-based on this very array — takes the
      // dangling "·" with it, so the block shows a bare "92". An unflagged part
      // is untouched: a real 0 still prints.
      .filter(({ cell }) => !skipEmptyCell(cell, cell.value))
      .map(({ field, cell }) => ({
        key: field.field,
        label: partLabel(field, labelText),
        text: formatCellValue(field, cell.value, record),
      }))
      .filter((part) => part.text !== '');
    // `hideWhenEmpty` (Detail Groups) on the pack's label field: a block with
    // nothing left to say disappears instead of rendering a bare heading with a
    // "—" under it. Unflagged blocks keep the previous behaviour exactly.
    if (!parts.length && !docCells.length && skipEmptyCell(labelField, undefined)) return null;
    // Same uniform grid cell as every other field (this used to emit an antd
    // <Col> into a CSS-grid container, which never sized correctly). A pack
    // carrying a document needs at least two tracks for the file chip/preview.
    // Size from the SCALAR line (an admin detailCol still wins), then widen to
    // two tracks when a document rides along, so the file chip has room without
    // every document pack eating a whole row.
    const joined = parts.map((part) => `${part.label ? `${part.label} ` : ''}${part.text}`).join('  ·  ');
    const textSpan = cellSpan({ detailCol: labelField.detailCol, value: joined });
    const span = docCells.length ? Math.max(textSpan, 2) : textSpan;
    return (
      <div
        key={`${labelField.field}-merged${keySuffix}`}
        className="v1-detail-cell v1-detail-cell--wrap"
        style={{ '--v1-cell-span': span }}
      >
        <div className="v1-detail-field">
          <Text type="secondary" className="v1-detail-label" title={labelText}>
            {labelText}
          </Text>
          {(parts.length > 0 || docCells.length === 0) && (
            <div className="v1-detail-value v1-detail-parts">
              {parts.length ? parts.map((part, i) => (
                <span className="v1-detail-part" key={`${part.key}-part`}>
                  {i > 0 && <span className="v1-detail-part-sep" aria-hidden="true">·</span>}
                  {part.label && <span className="v1-detail-part-label">{part.label}</span>}
                  <span>{part.text}</span>
                </span>
              )) : '—'}
            </div>
          )}
          {docCells.map((cell) => (
            <FieldValue key={`${cell.field}-doc`} field={cell} />
          ))}
        </div>
      </div>
    );
  };
  const renderFieldCells = (fields, buildCell, keySuffix = '', record = recordValueMap) => {
    const used = new Set();
    const out = [];
    fields.forEach((field) => {
      const ck = field.combineKey?.trim?.();
      if (ck) {
        if (used.has(ck)) return;
        used.add(ck);
        const pack = fields.filter((f) => f.combineKey?.trim?.() === ck);
        if (pack.length > 1) {
          const labelField = pack.find((f) => f.combineLabel) ?? pack[0];
          const merged = renderMergedCell(pack, labelField, buildCell, keySuffix, record);
          if (merged) out.push(merged);
          return;
        }
      }
      const cell = buildCell(field);
      // Per-field `hideWhenEmpty` (Detail Groups): an empty value the admin
      // marked as "not worth a row" is dropped rather than shown as "-".
      if (skipEmptyCell(cell, cell.value)) return;
      out.push(renderField(cell, keySuffix, record));
    });
    return out;
  };

  // Central documents grouped by their fieldName (the clean form-field key the
  // upload registered them under — e.g. "resume", "passport", or a group's
  // fileKey like "documents"). Each carries the presigned fileUrl.
  const centralDocsByField = useMemo(() => {
    const map = {};
    for (const d of centralDocs) {
      const fn = d.fieldName || d.field || 'file';
      (map[fn] ??= []).push({
        location: d.s3Key ?? d.location ?? d.uniqueName ?? '',
        name: d.fileName ?? d.name ?? '',
        fileUrl: d.fileUrl ?? d.url ?? '',
      });
    }
    return map;
  }, [centralDocs]);

  // If the central `documents` collection has files for this file/document
  // field (matched by field key OR its fileKey), render them from that
  // collection's presigned URLs. Only activates when such files exist, so a
  // field with no registry entry keeps its existing inline behaviour.
  const isFileField = (field) => field.type === 'file' || field.type === 'document' || field.renderType === 'document';
  const centralDocsFor = (field) => centralDocsByField[field.field]
    || (field.fileKey && centralDocsByField[field.fileKey])
    || null;

  // ── document value helpers ─────────────────────────────────────────────────
  // A file lives in TWO places: inline on the record (the container object the
  // module's update handler writes — passport.passportCopyLocation, …) and, for
  // anything uploaded since the shared registry existed, in the central
  // `documents` collection. Neither alone is complete, so every file cell shows
  // the UNION of both, de-duplicated by S3 location (falling back to the file
  // name). Registry entries come first: they carry the presigned `fileUrl` that
  // actually opens a private-bucket file.
  // Identity is the ORIGINAL FILE NAME, not the storage path: the very same
  // upload is written to two different keys (the module record stores
  // "submissions/<id>/passport_<file>", the registry stores
  // "documents/submissions/<id>/<timestamp>_passport_<file>"), so keying on the
  // path would list every file twice. Both sides preserve the user's file name.
  const docIdentityOf = (doc) => String(doc?.name || doc?.location || '').trim().toLowerCase();
  const docIdentity = (item) => {
    const [doc] = extractDocuments(item);
    if (!doc || (!doc.location && !doc.url)) return null;
    return docIdentityOf(doc);
  };
  const mergeDocValues = (inlineValue, registryDocs = []) => {
    const seen = new Set();
    const out = [];
    const add = (item) => {
      if (item == null || item === '') return;
      const key = docIdentity(item);
      // Not a file at all (e.g. a passport container holding only a number and
      // an expiry date) — nothing to list.
      if (key === null) return;
      if (key && seen.has(key)) return;
      if (key) seen.add(key);
      out.push(item);
    };
    registryDocs.forEach(add);
    (Array.isArray(inlineValue) ? inlineValue : [inlineValue]).forEach(add);
    return out;
  };
  // Every document a set of rows already displays inline, so the group-level
  // leftover list never repeats a file that is shown on its row.
  const docKeysOf = (value) => extractDocuments(value).map(docIdentityOf).filter(Boolean);

  // How many files this field HOLDS — mirrors the upload control's own capacity
  // rule (maxFiles validation → multiple/maxCount → single). The registry keeps
  // every version ever uploaded, so a single-file field (Passport, Visa, …)
  // must show the CURRENT file, not its whole upload history; a genuinely
  // multi-file field still shows its full set.
  const fileCapacity = (field) => {
    const maxFilesRule = (field.validations ?? []).find((rule) => rule?.type === 'maxFiles');
    if (maxFilesRule) return Math.max(1, Number(maxFilesRule.value ?? 1));
    if (truthy(field.multiple)) return Math.max(1, Number(field.maxCount ?? 5));
    return Math.max(1, Number(field.maxCount ?? 1));
  };

  // Trim a merged file list to the field's capacity, newest first. The record's
  // own inline value is authoritative for "current", so when it names a file
  // that file leads; otherwise the registry's newest entry does (the API sorts
  // createdAt descending).
  const limitDocs = (field, merged, inlineValue) => {
    const capacity = fileCapacity(field);
    if (merged.length <= capacity) return merged;
    const [current] = docKeysOf(inlineValue);
    const ordered = current
      ? [...merged].sort((a, b) => Number(docIdentity(b) === current) - Number(docIdentity(a) === current))
      : merged;
    return ordered.slice(0, capacity);
  };

  // For an addRow row, build a per-cell pseudo-field carrying that row's value.
  // File/document fields get the whole row as their value (so the render engine's
  // extractDocuments finds the stored file's location/name) and are forced to the
  // document render type; every other field reads its key off the row.
  const cellFieldForRow = (field, row) => {
    if (field.type === 'file' || field.type === 'document') {
      return { ...field, renderType: 'document', value: row };
    }
    // A RENAMED field is stored under its payloadKey, not its config key — e.g.
    // the employer group's isVMSRequired → isVmsRequired, vmsCommission →
    // vmsPercentage, vmsCommissionType → vmsType. Reading only field.field made
    // every such cell render "-" even though the row held the value. Same
    // field → payloadKey precedence the rest of the stack uses.
    const value = [field.field, field.payloadKey]
      .filter(Boolean)
      .map((key) => getDeep(row, key))
      .find((v) => v !== undefined && v !== null);
    return { ...field, value };
  };

  // Flat file field → inline record value + the central registry's files.
  const withCentralDocs = (field) => {
    if (!isFileField(field)) return field;
    const merged = limitDocs(field, mergeDocValues(field.value, centralDocsFor(field) ?? []), field.value);
    return merged.length ? { ...field, renderType: 'document', value: merged } : field;
  };

  // Repeatable (addRow) group → one indexed sub-block per stored row. The backend
  // embeds the full row array on group.rows (workExperience / educationDetails /
  // documents). Renders nothing extra when there are no rows.
  const renderAddRowGroup = (group, index, fields) => {
    const key = group.name ?? group.id ?? String(index);
    const rows = Array.isArray(group.rows) ? group.rows : [];
    // Files a repeatable group registered in the central `documents` collection
    // carry only the group's fileKey — the row index is stripped when they are
    // registered, so they cannot be attributed to a specific row. Rather than
    // guess (and risk showing row 2's file on row 1), list whatever is NOT
    // already displayed inline on a row as the group's own attachment list. A
    // group whose rows carry their files inline shows nothing extra here.
    const rowDocKeys = new Set(rows.flatMap((row) => docKeysOf(row)));
    const leftoverDocs = [];
    const leftoverSeen = new Set();
    fields.filter(isFileField).forEach((field) => {
      (centralDocsFor(field) ?? []).forEach((doc) => {
        const id = docIdentityOf(doc);
        if (!id || rowDocKeys.has(id) || leftoverSeen.has(id)) return;
        leftoverSeen.add(id);
        leftoverDocs.push(doc);
      });
    });
    return (
      <Card
        size="small"
        key={key}
        className="v1-card v1-detail-card"
        title={<SectionTitle icon={sectionIcons[index % sectionIcons.length]} title={group.label ?? group.name ?? 'Details'} />}
      >
        {rows.length === 0 && leftoverDocs.length === 0 ? (
          // Admin-configurable (Detail Groups → group → Empty text). Falls back
          // to a sentence derived from the group's own label, so a new
          // repeatable group reads correctly with no configuration at all.
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={resolveGroupEmptyText(group)} />
        ) : (
          rows.map((row, rowIndex) => (
            <div key={rowIndex} className="v1-detail-addrow-item">
              {rows.length > 1 && (
                <Text strong className="v1-detail-addrow-index">
                  {(group.label ?? 'Record')} {rowIndex + 1}
                </Text>
              )}
              <div className="v1-detail-grid">
                {renderFieldCells(fields, (f) => cellFieldForRow(f, row), `-${rowIndex}`, row)}
              </div>
            </div>
          ))
        )}
        {leftoverDocs.length > 0 && (
          <div className="v1-detail-addrow-item">
            <div className="v1-detail-grid">
              {renderField({
                field: `${key}-files`,
                label: 'Uploaded Files',
                renderType: 'document',
                detailCol: 24,
                value: leftoverDocs,
              })}
            </div>
          </div>
        )}
      </Card>
    );
  };

  const renderGroup = (group, index) => {
    const key = group.name ?? group.id ?? String(index);
    // Hide the raw description field — it gets its own section for jobs.
    const fields = jobDescription
      ? group.fields.filter((field) => field.field !== jobDescription.key)
      : group.fields;
    if (!fields.length) return null;

    if (group.addRow) return renderAddRowGroup(group, index, fields);

    return (
      <Card
        size="small"
        key={key}
        className="v1-card v1-detail-card"
        title={<SectionTitle icon={sectionIcons[index % sectionIcons.length]} title={group.label ?? group.name ?? 'Details'} />}
      >
        <div className="v1-detail-grid">{renderFieldCells(fields, withCentralDocs)}</div>
      </Card>
    );
  };

  // One merged "Rate Details" card holding every rate-history sub-section
  // (Candidate Rate, Proposed Rate) in the SAME "$value / Duration" format.
  const renderRateDetailsCard = () => (
    <Card
      size="small"
      key="rate-details-history"
      className="v1-card v1-detail-card"
      title={<SectionTitle icon={<HistoryOutlined />} title={historyCardTitle} />}
    >
      {historyBlocks.map((block, bi) => (
        <div className={`v1-rh-section${bi > 0 ? ' v1-rh-section--gap' : ''}`} key={block.key}>
          <div className="v1-rh-section-title">{block.label}</div>
          <div className="v1-rate-history">
            {block.entries.map((e, i) => (
              <div className={`v1-rh-item${i === 0 ? ' v1-rh-latest' : ''}`} key={i}>
                <span className="v1-rh-amount">{e.amount}</span>
                {e.unit && <span className="v1-rh-unit">/ {e.unit}</span>}
                {e.by && <span className="v1-rh-chip"><UserOutlined /> {e.by}</span>}
                {e.when && <span className="v1-rh-chip"><ClockCircleOutlined /> {e.when}</span>}
                {i === 0 && <span className="v1-rh-badge">Latest</span>}
              </div>
            ))}
          </div>
        </div>
      ))}
    </Card>
  );

  // Where the history card goes. `group.historyAfterGroup` (admin config) names
  // the group it should follow; with none configured it falls back to the
  // employer group — by name or by carrying employerId — which is where every
  // module placed it before the setting existed.
  const historyAfterGroupName = useMemo(() => {
    const owner = formGroups.find((g) => truthy(g.appendHistory) && g.historyAfterGroup);
    return String(owner?.historyAfterGroup ?? '').trim().toLowerCase();
  }, [formGroups]);

  const isEmployerGroup = (group) => {
    if (historyAfterGroupName) {
      return String(group.name ?? '').trim().toLowerCase() === historyAfterGroupName;
    }
    return /current_employer|employer_details/i.test(group.name || '')
      || (group.fields || []).some((f) => f.field === 'employerId');
  };

  return (
    <Spin spinning={loading} tip="Loading detail fields...">
      <div className="v1-detail-left">
        {error && (
          <Alert
            type="error"
            showIcon
            message="Unable to load detail fields"
            description={error}
            className="v1-detail-alert"
          />
        )}

        {!loading && !error && displayGroups.length === 0 && historyBlocks.length === 0 && (
          <Empty description="No detail fields are configured." />
        )}
        {/* Groups, with the merged "Rate Details" history card injected right
            after the Employer Details group (driven by appendHistory config). */}
        {displayGroups.flatMap((group, index) => {
          const els = [renderGroup(group, index)];
          if (isEmployerGroup(group) && historyBlocks.length > 0) els.push(renderRateDetailsCard());
          return els;
        })}
        {/* No employer group on this module → show Rate Details at the end. */}
        {historyBlocks.length > 0 && !displayGroups.some(isEmployerGroup) && renderRateDetailsCard()}

        {/* Description — jobs module only, displayed exactly as saved. */}
        {isJob && jobDescription?.value && (
          <Card
            size="small"
            className="v1-card v1-detail-card"
            title={<SectionTitle icon={<FileTextOutlined />} title="Description" />}
            // extra={
            //   !descEditing && (
            //     <AppButton type="text" size="small" icon={<EditOutlined />} onClick={handleDescEditStart}>
            //       Edit
            //     </AppButton>
            //   )
            // }
          >
            {descEditing ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={{
                  background: '#ffffff',
                  border: '1px solid #d6e4f0',
                  borderRadius: 8,
                  padding: '4px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                }}>
                  <Input.TextArea
                    rows={10}
                    value={descEditRaw}
                    onChange={(e) => setDescEditRaw(e.target.value)}
                    placeholder={jobDescription.hidePlaceholder ? undefined : jobDescription.placeholder}
                    style={{
                      fontSize: 13,
                      lineHeight: 1.65,
                      border: 'none',
                      boxShadow: 'none',
                      resize: 'vertical',
                      padding: '12px 14px',
                    }}
                  />
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <AppButton variant="primary" size="small" loading={descSaving} onClick={handleDescEditSave}>
                    Save & Parse
                  </AppButton>
                  <AppButton size="small" onClick={handleDescEditCancel}>Cancel</AppButton>
                </div>
              </div>
            ) : (
              <RawDescription value={jobDescription.value} />
            )}
          </Card>
        )}
      </div>
    </Spin>
  );
}
